import { pickVibeImage } from './pickVibeImage';
import { guided } from '@/llm/guidedRuntime';
import { parsePlan } from '@/llm/planLayouts';
import { PLAN_SYSTEM_PROMPT } from '@/llm/prompt/plan-prompt';
import { nearestKitId } from '@/webapp/styleFromImage';
import type { StyleBrief } from '@/llm/planTypes';
import { isCreditRefusal, isSignInRefusal } from './rankKitsFromFeedback';

/** See `SIGN_IN_TO_DESCRIBE_A_LOOK`: the proxy refuses a caller with no session. */
export const SIGN_IN_TO_MATCH_A_PICTURE = 'Sign in to match a look to a picture. Or pick one of these three.';

/**
 * What an account that cannot pay is told. It was "Could not read that picture just now" — a
 * permanent condition called a passing one, which sends somebody to pick photo after photo.
 */
export const NO_CREDITS_TO_MATCH_A_PICTURE =
  'This account does not have the credits to match a picture. Pick one of these three instead.';

/**
 * "Show me the look you want" — a reference picture turned into a choice.
 *
 * This existed once and was lost: `choose-layout.tsx` was deleted, and `pickVibeImage`,
 * `setVibeImages` and `setPlanResult` have sat in the codebase called by nothing ever since.
 * The prompt that reads a picture is still here and still says what to do with one ("If an
 * IMAGE is provided, derive styleBrief from it"), so this is a rewiring, not an invention.
 *
 * It goes through the ordinary `/llm` proxy rather than a new server route, because the proxy
 * already accepts an image — its own field comment calls this exact use "plan-first style
 * extraction". So nothing here needs deploying.
 *
 * The COLOURS are not handed to the build. They choose the kit, and the kit carries colour the
 * way the runtime expects; see `styleFromImage`.
 */

export type PickedStyle =
  | { kind: 'style'; styleBrief: StyleBrief; kitId: string }
  | { kind: 'failed'; reason: string };

export interface PickStyleDeps {
  pick?: () => Promise<string | null>;
  generate?: (input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
    image?: string;
    lookMatch?: boolean;
  }) => Promise<{ content: string }>;
}

/**
 * Returns null when the person cancelled the picker — not a failure, and it must not be
 * reported as one. Anything that genuinely went wrong comes back as `failed` with a reason
 * somebody can act on.
 */
export async function pickStyleFromPicture(
  goal: string,
  signedIn: boolean,
  deps: PickStyleDeps = {},
): Promise<PickedStyle | null> {
  const pick = deps.pick ?? pickVibeImage;
  // Said BEFORE the photo picker opens: choosing a picture only to be refused afterwards
  // wastes the one thing the visitor did.
  if (!signedIn) return { kind: 'failed', reason: SIGN_IN_TO_MATCH_A_PICTURE };

  let image: string | null;
  try {
    image = await pick();
  } catch {
    return { kind: 'failed', reason: 'Could not open your photos just now.' };
  }
  if (!image) return null;

  let content: string;
  try {
    const generate = deps.generate ?? ((input) => guided().generate(input));
    const res = await generate({
      systemPrompt: PLAN_SYSTEM_PROMPT,
      // The goal travels with the picture: the same photo means different things for a
      // scripture reader and a bike-repair log, and the model is being asked for a look that
      // suits BOTH.
      messages: [{ role: 'user', content: `Idea: ${goal}` }],
      signedIn,
      image,
      /*
       * Says what this call IS, because `image` alone reads as an edit.
       *
       * `/llm` prices any request carrying a picture as an edit — 500 credits, taken before the
       * model runs, and never covered by the free first build — so this button cost a new
       * account 500 of its 714 free credits while the note under it said the build was free.
       * Owner's rule: the wizard's helper calls are not billed; the app generation is. The flag
       * lets the proxy tell a wizard helper from an in-editor edit that sends a screenshot, and
       * price it as the first (free, but a balance must be behind it — hence the refusal below).
       */
      lookMatch: true,
    });
    content = res.content;
  } catch (err) {
    if (isSignInRefusal(err)) return { kind: 'failed', reason: SIGN_IN_TO_MATCH_A_PICTURE };
    if (isCreditRefusal(err)) return { kind: 'failed', reason: NO_CREDITS_TO_MATCH_A_PICTURE };
    return { kind: 'failed', reason: 'Could not read that picture just now. Your plan is unchanged.' };
  }

  const parsed = parsePlan(content);
  if (!parsed?.styleBrief) {
    // A reply we cannot parse must not become a half-applied look — the plan is left exactly
    // as it was, and the person is told, rather than seeing a kit change for no visible reason.
    return { kind: 'failed', reason: 'That picture did not give a clear look. Try another one.' };
  }

  return {
    kind: 'style',
    styleBrief: parsed.styleBrief,
    kitId: nearestKitId(parsed.styleBrief.palette.primary),
  };
}
